import React from 'react'
import { NavLink } from 'react-router'
import { useTranslation } from 'react-i18next'
import {
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  User,
  LogOut,
  MessageSquare,
  AlertCircle,
} from 'lucide-react'
import { cn } from '@/shared/lib/variants'
import { getConfig } from '@/shared/config/runtime-env'
import { Badge, Button, Popover, PopoverContent, PopoverTrigger, RoleBadge, Text, type SystemRole } from '@agion/ui'
import { useAuth } from '@/shared/auth/unified-auth'
import { useThemeContext } from '@/app/providers/ThemeProvider'
import { usePermissions, type NavigationPermissions } from '@/shared/auth/permission-context'
import { OrgSwitcher } from '@/shared/ui/molecules/OrgSwitcher'
import { AppSwitcher } from '@/shared/ui/molecules/AppSwitcher'
import AgionLogo from '@/assets/agion-logo.svg'
import AgionLogoDark from '@/assets/agion-logo-dark.svg'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@agion/ui'
import { AvatarWithInitials } from '@/components/shared/avatar-with-initials'
import { navigation, partitionNavChildren, type NavItem } from './sidebarNavigation'
import { useModelDefaults } from '@/hooks/useResourceData'

// Navigation items with permission requirements
// Items without permission are always visible
// Items with permission are filtered based on user's permissions
// Collect all navigation hrefs to determine which items need exact matching.
// Items whose href is a prefix of another item's href need exact matching (end=true)
// to avoid false highlights (e.g., /governance must not highlight for /governance/trust).
// All other items use prefix matching (end=false) so subpages stay highlighted.
function computeExactMatchPaths(items: NavItem[]): Set<string> {
  const allHrefs: string[] = items.flatMap(item =>
    item.children?.map(c => c.href).filter((h): h is string => !!h) ?? (item.href ? [item.href] : [])
  )
  const exactMatch = new Set<string>()
  for (const href of allHrefs) {
    if (allHrefs.some(other => other !== href && other.startsWith(href + '/'))) {
      exactMatch.add(href)
    }
  }
  return exactMatch
}

const exactMatchPaths = computeExactMatchPaths(navigation)

// Alerts indicate that a child route needs operator attention. Rendered as a
// small red AlertCircle next to the child link (or on the parent's icon when
// the sidebar is collapsed so it stays visible without opening the popover).
type AlertMap = Record<string, string | undefined>

const NavAlertIcon: React.FC<{ label: string }> = ({ label }) => (
  <AlertCircle
    className="h-4 w-4 flex-shrink-0 text-red-600 dark:text-red-400"
    aria-label={label} aria-hidden={false}
  />
)

const NavSection: React.FC<{
  item: NavItem
  collapsed: boolean
  canNavigateTo: (page: keyof NavigationPermissions) => boolean
  isReadOnly: boolean
  alerts: AlertMap
}> = ({ item, collapsed, canNavigateTo, isReadOnly, alerts }) => {
  const { t } = useTranslation('admin')
  const { user } = useAuth()
  const [isOpen, setIsOpen] = React.useState(true)

  // Filter children based on permissions and read-only status
  const visibleChildren = item.children?.filter(child => {
    // Org-role allowlist: a sensitive item (e.g. entity resolution, which
    // exposes/merges cross-source PII) is hidden from any role not in the list,
    // regardless of feature permission.
    if (child.requiredRoles && !(user && child.requiredRoles.includes(user.role))) return false
    // SP2 C3: a runtime feature gate hides the item regardless of permissions
    // (e.g. Resolution on a reboot host at cutover). Evaluated per render so a
    // ConfigMap flag flip is honored live.
    if (child.featureFlag && !child.featureFlag()) return false
    // If no permission required, always show (unless requires write access and user is read-only)
    if (!child.permission) {
      if (child.requiresWriteAccess && isReadOnly) return false
      return true
    }
    // Hide write-access items from read-only users
    if (child.requiresWriteAccess && isReadOnly) return false
    // Check if user can navigate to this page
    return canNavigateTo(child.permission)
  })

  // Specialist entries render after the core ones, under an Advanced label.
  const { core: coreChildren, advanced: advancedChildren } = partitionNavChildren(visibleChildren ?? [])
  const orderedChildren = [...coreChildren, ...advancedChildren]
  const firstAdvancedChild = advancedChildren[0]

  const hasChildren = visibleChildren && visibleChildren.length > 0

  // Show parent-level alert dot when collapsed and any visible child has one,
  // so the warning stays visible without opening the popover.
  const childAlert = visibleChildren?.find(c => c.href && alerts[c.href])
  const parentAlertLabel = childAlert?.href ? alerts[childAlert.href] : undefined

  if (!hasChildren && item.href) {
    const ownAlertLabel = alerts[item.href]
    return (
      <NavLink
        to={item.href}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors",
            "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 dark:focus-visible:ring-slate-600",
            isActive
              ? "bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100"
              : "text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/30"
          )
        }
        title={collapsed ? t(`navigation.${item.nameKey}`) : undefined}
      >
        <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        {!collapsed && (
          <>
            <span className="flex-1">{t(`navigation.${item.nameKey}`)}</span>
            {ownAlertLabel && <NavAlertIcon label={ownAlertLabel} />}
            {item.badge && (
              <Badge variant="error" size="sm">
                {item.badge}
              </Badge>
            )}
          </>
        )}
        {collapsed && ownAlertLabel && <NavAlertIcon label={ownAlertLabel} />}
      </NavLink>
    )
  }

  // Section header with children
  // When collapsed, show a popover flyout with child links
  if (collapsed) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            className="w-full flex items-center justify-center px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 relative"
            title={t(`navigation.${item.nameKey}`)}
          >
            <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            {parentAlertLabel && (
              <span
                className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-red-600 dark:bg-red-400 ring-2 ring-white dark:ring-slate-800"
                aria-label={parentAlertLabel}
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent side="right" align="start" sideOffset={8} className="w-48 p-1">
          <Text as="p" size="xs" weight="semibold" className="px-3 py-1.5 text-slate-500 dark:text-slate-400">
            {t(`navigation.${item.nameKey}`)}
          </Text>
          {visibleChildren?.map((child) => {
            const alertLabel = child.href ? alerts[child.href] : undefined
            return (
              <NavLink
                to={child.href!}
                end={exactMatchPaths.has(child.href!)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 dark:focus-visible:ring-slate-600",
                    isActive
                      ? "bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100"
                      : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/30"
                  )
                }
              >
                <child.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span className="flex-1">{t(`navigation.${child.nameKey}`)}</span>
                {alertLabel && <NavAlertIcon label={alertLabel} />}
              </NavLink>
            )
          })}
        </PopoverContent>
      </Popover>
    )
  }

  // Expanded: show inline collapsible children
  return (
    <div className="space-y-1">
      <Button
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300"
        aria-expanded={isOpen}
        aria-label={`${isOpen ? t('admin:aria.collapse') : t('admin:aria.expand')} ${t(`navigation.${item.nameKey}`)}`}
      >
        <item.icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <Text as="span" weight="semibold" className="flex-1 text-left">{t(`navigation.${item.nameKey}`)}</Text>
        {isOpen ? (
          <ChevronDown className="h-4 w-4" />
        ) : (
          <ChevronRight className="h-4 w-4" />
        )}
      </Button>

      {isOpen && visibleChildren && (
        <div className="ml-7 space-y-1">
          {orderedChildren.map((child) => {
            const alertLabel = child.href ? alerts[child.href] : undefined
            return (
              <React.Fragment key={child.nameKey}>
              {child === firstAdvancedChild && (
                <Text
                  as="div"
                  size="xs"
                  weight="semibold"
                  className="px-3 pt-2 pb-0.5 uppercase tracking-wide text-slate-400 dark:text-slate-500"
                  data-testid={`nav-advanced-label-${item.nameKey}`}
                >
                  {t('navigation.advanced')}
                </Text>
              )}
              <NavLink
                key={child.nameKey}
                to={child.href!}
                end={exactMatchPaths.has(child.href!)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors",
                    "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 dark:focus-visible:ring-slate-600",
                    isActive
                      ? "bg-slate-100 text-slate-900 dark:bg-slate-700/50 dark:text-slate-100"
                      : "text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/30"
                  )
                }
              >
                <child.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span className="flex-1">{t(`navigation.${child.nameKey}`)}</span>
                {alertLabel && <NavAlertIcon label={alertLabel} />}
              </NavLink>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  )
}


interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
  showCollapseToggle?: boolean
}

export const Sidebar: React.FC<SidebarProps> = ({
  collapsed: sidebarCollapsed,
  onToggleCollapse,
  showCollapseToggle = true,
}) => {
  const { t } = useTranslation(['admin', 'common', 'components'])
  const { user, logout } = useAuth()
  const { canNavigateTo, permissions, isLoading, isReadOnly } = usePermissions()
  const { resolvedTheme } = useThemeContext()

  // Only fetch model defaults once permissions are known and the user has
  // access to the Resources page — otherwise the request is wasted and would
  // surface a 403 in the network tab.
  const canSeeResources = !isLoading && canNavigateTo('resources')
  const { data: modelDefaults } = useModelDefaults(canSeeResources)
  const chatDefault = modelDefaults?.defaults?.chat
  const chatDefaultMissing = canSeeResources && modelDefaults != null && !chatDefault
  // A default that exists but is degraded (isAvailable: false — the backend
  // could not resolve its Foundry route) is just as unusable as no default
  // at all, but the alert must say so rather than claim nothing is selected.
  const chatDefaultDegraded = canSeeResources && !!chatDefault && chatDefault.isAvailable === false

  const alerts: AlertMap = React.useMemo(() => ({
    '/governance/resources': chatDefaultMissing
      ? t('admin:navigation.alerts.defaultChatModelMissing', 'Default chat model not selected')
      : chatDefaultDegraded
      ? t('admin:navigation.alerts.defaultChatModelDegraded', 'Default chat model cannot be resolved')
      : undefined,
  }), [chatDefaultMissing, chatDefaultDegraded, t])

  const handleLogout = () => {
    logout()
  }

  // Filter top-level navigation items based on permissions
  const visibleNavigation = navigation.filter(item => {
    // If no permission required, always show
    if (!item.permission) return true
    // Check if user can navigate to this page
    return canNavigateTo(item.permission)
  })

  // Get role for display
  const userRole = (permissions?.role || user?.role || 'user') as SystemRole

  return (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex items-center justify-between h-14 pl-5 pr-3 mb-1">
        <div className="flex items-center gap-2.5">
          {!sidebarCollapsed && (
            <img
              src={resolvedTheme === 'dark' ? AgionLogoDark : AgionLogo}
              alt={t('admin:brand.name')}
              className="h-5 w-auto"
            />
          )}
        </div>
        {showCollapseToggle && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggleCollapse}
            aria-label={sidebarCollapsed ? t('components:layout.sidebar.expandSidebar', 'Expand sidebar') : t('components:layout.sidebar.collapseSidebar', 'Collapse sidebar')}
          >
            <ChevronLeft className={cn("h-4 w-4 transition-transform", sidebarCollapsed && "rotate-180")} />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2 py-1 space-y-0.5" aria-label={t('components:layout.sidebar.navigation.mainNavigation')}>
        {isLoading ? (
          <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">{t('common:loading', 'Loading...')}</div>
        ) : (
          visibleNavigation.map((item) => (
            <NavSection
              key={item.nameKey}
              item={item}
              collapsed={sidebarCollapsed}
              canNavigateTo={canNavigateTo}
              isReadOnly={isReadOnly()}
              alerts={alerts}
            />
          ))
        )}
      </nav>

      {/* Footer with org switcher and user */}
      <div className="border-t border-slate-200 dark:border-slate-700/50 p-2 space-y-1">
        {/* P-009: Organization Switcher */}
        <OrgSwitcher variant="sidebar" collapsed={sidebarCollapsed} />
        <AppSwitcher collapsed={sidebarCollapsed} />

        {/* Language switcher moved to /profile (Slice 3, 2026-05-08). */}

        {/* User Profile */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <div className={cn(
              "flex items-center gap-2.5 rounded-md px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors cursor-pointer",
              sidebarCollapsed && "justify-center px-0"
            )}>
              <AvatarWithInitials
                firstName={user?.firstName}
                lastName={user?.lastName}
                name={user?.name || user?.email}
                size="sm"
                className="flex-shrink-0"
              />
              {!sidebarCollapsed && (
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Text weight="medium" className="truncate">
                      {user?.name || user?.email || t('admin:user.defaultName')}
                    </Text>
                    {/* Role Badge - Using unified design system component */}
                    <RoleBadge role={userRole} size="sm" />
                  </div>
                  <Text size="xs" color="muted" className="truncate">
                    {user?.organizationName || t('admin:user.defaultOrganization')}
                  </Text>
                </div>
              )}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            side="top"
            align="end"
            className="w-56"
          >
            <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-700/50">
              <div className="flex items-center gap-3">
                <AvatarWithInitials
                  firstName={user?.firstName}
                  lastName={user?.lastName}
                  name={user?.name || user?.email}
                  size="md"
                  className="flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <Text weight="medium" className="truncate">
                    {user?.name || user?.email || t('admin:user.defaultName')}
                  </Text>
                  <div className="flex items-center gap-2">
                    <Text size="xs" color="muted" className="truncate">
                      {user?.organizationName || t('admin:user.defaultOrganization')}
                    </Text>
                    {/* Role Badge in dropdown - Using unified design system component */}
                    <RoleBadge role={userRole} size="sm" />
                  </div>
                </div>
              </div>
            </div>

            <div className="py-1">
              <DropdownMenuItem asChild>
                <NavLink
                  to="/profile"
                  className="flex items-center gap-3"
                >
                  <User className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <span>{t('navigation.profile')}</span>
                </NavLink>
              </DropdownMenuItem>

              <DropdownMenuItem asChild>
                <a
                  href={getConfig('VITE_APP_URL', 'http://localhost:5174')}
                  className="flex items-center gap-3"
                >
                  <MessageSquare className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                  <span>{t('admin:navigation.agentChat', 'Agent Chat')}</span>
                </a>
              </DropdownMenuItem>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-700/50 pt-1 pb-1">
              <DropdownMenuItem
                onClick={handleLogout}
                className="flex items-center gap-3 cursor-pointer"
              >
                <LogOut className="h-4 w-4 text-slate-500 dark:text-slate-400" />
                <span>{t('navigation.logout')}</span>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}
